<template>
    <div class="imagecanvas-main">
        <div class="imagecanvas-container">
            <div class="imagecanvas-image-canvas-container">
                <img :src="imgSrc" class="image" @load="initCanvas">
                <canvas ref="canvas" class="absolute-canvas" :class="{ 'no-pointer-events': readOnly }" @pointerdown="startDrag" tabindex="0"></canvas>

                <div v-if="readOnly" class="canvas-toolbar">
                    <img class="toolbar-btn" :src="showPoints ? require('../assets/enableView.png') : require('../assets/disableView.png')"
                    :title="showPoints ? 'Ocultar pontos' : 'Mostrar pontos'"
                    @click="togglePoints"
                    alt="toggle points"
                    />
                </div>                
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import { defineComponent, ref, onMounted, onBeforeUnmount, watch } from "vue";
    import { Line, LineInfo, Point, Lines, ImageInfo } from "../helpers/Interfaces";
    import ProjectService from "@/services/Project";
    import { subscriptionManager } from "@/realtime/SubscriptionManager";

    export default defineComponent({
        name: "ImageCanvas",
        props: {
            image:{
                type: Object as () => ImageInfo | null,
                default: null
            },
            highLightedLine: {
                type: Object as () => Line | null,
                default: () => []
            },
            readOnly: { 
                type: Boolean, default: false 
            }
        },
        setup(props, {emit, expose}) {
            const canvas = ref<HTMLCanvasElement | null>(null); // Canvas to draw elements
            const offscreenCanvas = ref<HTMLCanvasElement | null>(null); // Off-screen canvas for zoomed drawing
            const img = ref<HTMLImageElement | null>(null); // Reference to the image element
            const lines = ref<Lines>({ canvasWidth: 0, canvasHeight: 0, lines: []});
            const originalImageSize = ref({ width: 0, height: 0 });
            const lastCanvasSize = ref({ width: 0, height: 0 });
            const draggedLine = ref<LineInfo | null>(null); // Start or End point being dragged
            const hoverLine = ref<LineInfo | null>(null); // Start or end point being hovered
            const ctrl_Pressed = ref(false); // Track CTRL key
            const shift_Pressed = ref(false); // Track SHIFT key
            const s_Pressed = ref(false); // Track SHIFT + S key
            const z_Pressed = ref(false); // Tracks if 'Z' key is pressed
            const d_Pressed = ref(false); // Tracks if 'D' key is pressed
            const a_Pressed = ref(false); // Tracks if 'A' key is pressed
            const w_Pressed = ref(false); // Tracks if 'W' key is pressed
            const q_Pressed = ref(false); // Tracks if 'Q' key is pressed
            const h_Pressed = ref(false); // Tracks if 'H' key is pressed
            let tempStartPoint:Point|null = null; // Stores first point of a line
            const lineMoveActive = ref<Boolean>(false);
            const allLinesMoveActive = ref<Boolean>(false);
            const deleteLineActive = ref<Boolean>(false); // Tracks if delete line is active
            const zoomActive = ref<Boolean>(false); // Tracks if zoom is active
            const mousePosition = ref({ x: 0, y: 0 }); // Tracks mouse position
            const draggingLines = ref<Line[]>([]);
            const dirtyLines = ref<Map<number, Line>>(new Map());
            const locallyUpdatedLines = new Set<number>(); // Keeps track of the updated line ids
            const imgSrc = ref<string>("");
            const activeImageId = ref<number | null>(null);
            const showPoints = ref(true);
            const hidePointsTemp = ref(false);
            let annoUnsubs: Array<() => void> = [];
            let watchToken = 0;

            // Checks if image changed and sets new image properties
            watch(() => props.image, async (newImage) => {
                const token = ++watchToken;

                annoUnsubs.forEach(off => off());
                annoUnsubs = [];

                if (!newImage?.id || !newImage.project_id) {
                    activeImageId.value = null;
                    imgSrc.value = "";
                    lines.value = { canvasWidth: 0, canvasHeight: 0, lines: [] };
                    return;
                }

                activeImageId.value = newImage.id;

                const ann = await ProjectService.getAnnotationsForImage(newImage.id);
                lines.value = {
                    canvasWidth: canvas.value?.width || 0,
                    canvasHeight: canvas.value?.height || 0,
                    lines: ann,
                };

                imgSrc.value = ProjectService.getImageUrl(newImage.project_id, newImage.file_name);
            }, { immediate: true });

            watch(() => props.highLightedLine, () => {
                redrawCanvas();
            });

            async function updateAnnotations(imageId: number, lines: Line[]) {
                try {
                    lines.forEach(line => locallyUpdatedLines.add(line.id));
                    await ProjectService.updateAnnotation(imageId, lines);
                } catch (error) {
                    console.error("Erro ao atualizar anotações:", error);
                }
            }

            async function initCanvas() {
                if (!canvas.value) return;

                const currentId = props.image?.id ?? null;
                if (!currentId || currentId !== activeImageId.value) return;

                const imgElement = canvas.value.parentElement!.querySelector(".image") as HTMLImageElement;
                if (!imgElement) return;

                const myToken = watchToken;

                const setupCanvas = async () => {
                    if (imgElement.clientWidth === 0 || imgElement.clientHeight === 0) return;

                    canvas.value!.width = imgElement.clientWidth;
                    canvas.value!.height = imgElement.clientHeight;

                    offscreenCanvas.value = document.createElement("canvas");
                    offscreenCanvas.value.width = canvas.value!.width;
                    offscreenCanvas.value.height = canvas.value!.height;

                    originalImageSize.value.width = imgElement.naturalWidth;
                    originalImageSize.value.height = imgElement.naturalHeight;

                    canvas.value!.onmousemove = handleMouseMove;

                    redrawCanvas();

                    // Subscribes only if this init is active (same token)
                    if (myToken === watchToken && currentId === activeImageId.value) {
                        await subscribeImageAnnotations(currentId);
                    }
                };
                img.value = imgElement;
                if (imgElement.complete) setupCanvas();
                else imgElement.onload = () => setupCanvas();
            }

            async function subscribeImageAnnotations(imageId: number) {
                // Cleanup previous listeners
                annoUnsubs.forEach(off => off());
                annoUnsubs = [];

                const topic = subscriptionManager.topicAnnotations(imageId);

                // INSERT
                annoUnsubs.push(await subscriptionManager.subscribe(topic, "annotations:insert", ({ line }) => {
                    // Avoid duplicates
                    if (!lines.value.lines.some(l => l.id === line.id)) {
                    lines.value.lines.push(line);
                    redrawCanvas();
                    }
                }));

                // UPDATE
                annoUnsubs.push(await subscriptionManager.subscribe(topic, "annotations:update", ({ line }) => {
                    if (locallyUpdatedLines.has(line.id)) {
                    // Ignore my local echo
                    locallyUpdatedLines.delete(line.id);
                    return;
                    }
                    const idx = lines.value.lines.findIndex(l => l.id === line.id);
                    if (idx >= 0) {
                    lines.value.lines[idx] = line;
                    redrawCanvas();
                    }
                }));

                // DELETE
                annoUnsubs.push(await subscriptionManager.subscribe(topic, "annotations:delete", ({ id }) => {
                    lines.value.lines = lines.value.lines.filter(l => l.id !== id);

                    dirtyLines.value.delete(id);
                    locallyUpdatedLines.delete(id);
                    if (draggedLine.value?.line?.id === id) draggedLine.value = null;

                    redrawCanvas();
                }));
            }

            function markDirty(line: Line) {
                dirtyLines.value.set(line.id, line);
            }

            async function flushDirty(reason = '') {
                if (!props.image || !dirtyLines.value.size) return;

                // só envia se a linha ainda existe no estado atual
                const alive = new Set(lines.value.lines.map(l => l.id));
                for (const id of Array.from(dirtyLines.value.keys())) {
                    if (!alive.has(id)) dirtyLines.value.delete(id);
                }
                const payload = Array.from(dirtyLines.value.values());
                if (!payload.length) return;

                try {
                    await updateAnnotations(props.image.id, payload);
                } finally {
                    dirtyLines.value.clear();
                }
            }

            function findPointAt(x: number, y: number): LineInfo | null {
                if (!canvas.value) return null;
                const radius = 5;
                for (const line of lines.value?.lines ?? []) {
                    for (const pointType of ["start", "middle", "end"] as const) {
                        const p = toCanvasCoords(line[pointType]);
                        const dx = x - p.x, dy = y - p.y;
                        if (Math.sqrt(dx*dx + dy*dy) <= radius) {
                            return { line, pointType };
                        }
                    }
                }
                return null;
            }

            function lerp(p:{x:number;y:number}, q:{x:number;y:number}, t:number) {
                return { x: p.x + (q.x - p.x)*t, y: p.y + (q.y - p.y)*t };
            }

            function splitQuadratic(line: Line, t = 0.5): [Omit<Line,"id">, Omit<Line,"id">] {
                const P0 = { x: line.start.x,  y: line.start.y  };
                const P1 = { x: line.middle.x, y: line.middle.y };
                const P2 = { x: line.end.x,    y: line.end.y    };

                const A = lerp(P0, P1, t);
                const B = lerp(P1, P2, t);
                const R = lerp(A, B, t);

                const R1 = { x: R.x, y: R.y };
                const R2 = { x: R.x, y: R.y };

                const first: Omit<Line,"id"> = {
                    label: line.label,
                    start: { x: P0.x, y: P0.y },
                    middle:{ x: A.x,  y: A.y  },
                    end:   R1,
                };

                const second: Omit<Line,"id"> = {
                    label: line.label,
                    start: R2,
                    middle:{ x: B.x,  y: B.y  },
                    end:   { x: P2.x, y: P2.y },
                };

                return [first, second];
            }

            // Converts coordinates relative to canvas size from original image size
            function toCanvasCoords(point: Point): Point {
                if (!canvas.value || !originalImageSize.value.width || !originalImageSize.value.height) {
                    return { x: point.x, y: point.y };
                }
                const { width: naturalW, height: naturalH } = originalImageSize.value;
                const { width: canvasW, height: canvasH } = canvas.value;

                return {
                    x: (point.x / naturalW) * canvasW,
                    y: (point.y / naturalH) * canvasH
                };
            }

            // Converts coordinates relative to original size from canvas image size
            function toOriginalCoords(xCanvas: number, yCanvas: number): Point {
                if (!canvas.value || !originalImageSize.value.width || !originalImageSize.value.height) {
                    return { x: xCanvas, y: yCanvas };
                }
                const { width: naturalW, height: naturalH } = originalImageSize.value;
                const { width: canvasW, height: canvasH } = canvas.value;

                return {
                    x: (xCanvas / canvasW) * naturalW,
                    y: (yCanvas / canvasH) * naturalH
                };
            }            

            function drawLine(ctx: CanvasRenderingContext2D, point1:Point, point2:Point, middlePoint:Point, color:string, specialHighlight: boolean) {
                ctx.beginPath();
                ctx.moveTo(point1.x, point1.y);
                ctx.quadraticCurveTo(middlePoint.x, middlePoint.y, point2.x, point2.y);
                ctx.strokeStyle = color;
                ctx.lineWidth = specialHighlight ? 6 : 3;
                ctx.globalAlpha = specialHighlight ? 1 : 0.6;
                ctx.stroke();
                ctx.globalAlpha = 1;
            }

            function drawCircle(ctx: CanvasRenderingContext2D, point: Point, highlight:boolean) {
                ctx.beginPath();
                ctx.arc(point.x, point.y, 5, 0, 2 * Math.PI, false);
                ctx.fillStyle = highlight ? 'blue' : 'red';
                ctx.globalAlpha = 0.6;
                ctx.fill();
                ctx.globalAlpha = 1;
            }

            function drawLabelBubbleFromAnchor(ctx: CanvasRenderingContext2D, anchor: {x:number;y:number}, tangentVec: {x:number;y:number}, textRaw: string) {
                const text = (textRaw ?? "").toString().trim();
                if (!text || !canvas.value) return;

                const prevFont = ctx.font;
                ctx.font = '500 40px Inter, system-ui, -apple-system, "Segoe UI", Roboto, Arial';

                const n = normalize({ x: -tangentVec.y, y: tangentVec.x });
                const paddingX = 8, paddingY = 6;
                const baseOffset = 18;
                const marginCanvas = 6;

                const metrics = ctx.measureText(text);
                const textW = Math.ceil(metrics.width);
                const textH = 40;
                const boxW = textW + paddingX*2;
                const boxH = textH + paddingY*2;

                const cw = canvas.value.width, ch = canvas.value.height;

                const fitsInside = (x:number, y:number) =>
                    x >= marginCanvas && y >= marginCanvas &&
                    x + boxW <= (cw - marginCanvas) &&
                    y + boxH <= (ch - marginCanvas);

                // Tenta +normal e -normal
                const candidates = [+1, -1];
                let chosen: {x:number;y:number; side:number} | null = null;

                for (const side of candidates) {
                    const cx = anchor.x + n.x * baseOffset * side;
                    const cy = anchor.y + n.y * baseOffset * side;
                    const x = Math.round(cx - boxW/2);
                    const y = Math.round(cy - boxH/2);
                    if (fitsInside(x, y)) { chosen = { x, y, side }; break; }
                }

                // Se não coube, clamp no lado +normal e garante lado correto
                if (!chosen) {
                    const side = +1;
                    const cx = anchor.x + n.x * baseOffset * side;
                    const cy = anchor.y + n.y * baseOffset * side;
                    let x = Math.round(cx - boxW/2);
                    let y = Math.round(cy - boxH/2);

                    x = clamp(x, marginCanvas, cw - boxW - marginCanvas);
                    y = clamp(y, marginCanvas, ch - boxH - marginCanvas);

                    // Mantém o centro do rect no mesmo semiplano definido pela normal
                    const center = { x: x + boxW/2, y: y + boxH/2 };
                    const v = { x: center.x - anchor.x, y: center.y - anchor.y };
                    const sameSide = (v.x * n.x + v.y * n.y) * side >= 0;
                    if (!sameSide) {
                    const push = baseOffset;
                    const nx = n.x * push * side;
                    const ny = n.y * push * side;
                    x = clamp(Math.round(center.x + nx - boxW/2), marginCanvas, cw - boxW - marginCanvas);
                    y = clamp(Math.round(center.y + ny - boxH/2), marginCanvas, ch - boxH - marginCanvas);
                    }
                    chosen = { x, y, side };
                }

                // Desenha a “bolha”
                const { x, y } = chosen!;
                ctx.save();
                roundedRectPath(ctx, x, y, boxW, boxH, 8);
                ctx.fillStyle = 'rgba(0,0,0,0.65)';
                ctx.fill();
                ctx.lineWidth = 1;
                ctx.strokeStyle = 'rgba(255,255,255,0.7)';
                ctx.stroke();

                ctx.fillStyle = '#fff';
                ctx.textBaseline = 'middle';
                ctx.textAlign = 'center';
                ctx.fillText(text, x + boxW/2, y + boxH/2);
                ctx.restore();

                ctx.font = prevFont;
            }

            function redrawCanvas(drawPoints: boolean = effectiveDrawPoints()) {
                if (!canvas.value || !offscreenCanvas.value) return;
                
                const context = (canvas.value as HTMLCanvasElement).getContext('2d')!;
                const offCtx = offscreenCanvas.value.getContext('2d')!;
                
                context.clearRect(0, 0, canvas.value.width, canvas.value.height); // Clears the canvas
                offCtx.clearRect(0, 0, offscreenCanvas.value.width, offscreenCanvas.value.height);

                // Draw the image on the off-screen canvas
                if (img.value) {
                    offCtx.drawImage(img.value, 0, 0, offscreenCanvas.value.width, offscreenCanvas.value.height);
                }

                if (tempStartPoint && drawPoints) { // If start point
                    const startScaled = toCanvasCoords(tempStartPoint);
                    drawCircle(offCtx, startScaled, true);
                }

                lines.value?.lines.forEach((line) => {
                    const startScaled = toCanvasCoords(line.start);
                    const middleScaled = toCanvasCoords(line.middle);
                    const endScaled = toCanvasCoords(line.end);

                    // Draw lines
                    const highLight = props.highLightedLine?.id === line.id;
                    let color = "purple";
                    if (deleteLineActive.value) {
                        color = hoverLine.value?.line === line ? "green" : "blue";
                    } else if (allLinesMoveActive.value) {
                        color = "cyan";
                    } else if (lineMoveActive.value) {
                        color = hoverLine.value?.line === line ? "orange" : "blue";
                    } else if (highLight || hoverLine.value?.line === line) {
                        color = "red";
                    }

                    drawLine(offCtx, startScaled, endScaled, middleScaled, color, highLight);

                    if (highLight && props.readOnly) {
                        const tangent = { x: endScaled.x - startScaled.x, y: endScaled.y - startScaled.y };
                        drawLabelBubbleFromAnchor(offCtx, middleScaled, tangent, String(line.label ?? ""));
                    }

                    // Draw circles of points start, middle and end
                    if (drawPoints) {
                        const types: Array<"start" | "middle" | "end"> = ["start", "middle", "end"];
                        types.forEach((pointType) => {
                            const scaled = toCanvasCoords(line[pointType]);
                            const isHovered =
                            hoverLine.value?.line === line && hoverLine.value?.pointType === pointType;
                            drawCircle(offCtx, scaled, isHovered);
                        });
                    }
                });

                // Draw the off-screen canvas onto the main canvas (normal view)
                context.drawImage(offscreenCanvas.value, 0, 0);

                // Draw the zoomed-in circle if zoom is active
                if (zoomActive.value) {
                    const zoomFactor = 3;
                    const radius = 100;

                    // Calculate the source rectangle on the off-screen canvas
                    let sx = mousePosition.value.x - radius / zoomFactor;
                    let sy = mousePosition.value.y - radius / zoomFactor;
                    let sWidth = (radius * 2) / zoomFactor;
                    let sHeight = (radius * 2) / zoomFactor;

                    // Calculate destination rectangle dimensions
                    let dx = mousePosition.value.x - radius;
                    let dy = mousePosition.value.y - radius;
                    let dWidth = radius * 2;
                    let dHeight = radius * 2;

                    // Adjust if the source rectangle goes beyond the off-screen canvas boundaries
                    if (sx < 0) {
                        // Adjust destination x position and width
                        const sxDiff = -sx;
                        sx = 0;
                        sWidth -= sxDiff;
                        dx += sxDiff * zoomFactor;
                        dWidth -= sxDiff * zoomFactor;
                    }
                    if (sy < 0) {
                        // Adjust destination y position and height
                        const syDiff = -sy;
                        sy = 0;
                        sHeight -= syDiff;
                        dy += syDiff * zoomFactor;
                        dHeight -= syDiff * zoomFactor;
                    }
                    if (sx + sWidth > offscreenCanvas.value.width) {
                        const overflow = (sx + sWidth) - offscreenCanvas.value.width;
                        sWidth -= overflow;
                        dWidth -= overflow * zoomFactor;
                    }
                    if (sy + sHeight > offscreenCanvas.value.height) {
                        const overflow = (sy + sHeight) - offscreenCanvas.value.height;
                        sHeight -= overflow;
                        dHeight -= overflow * zoomFactor;
                    }

                    // Clip the main canvas to a circle
                    context.save();
                    context.beginPath();
                    context.arc(mousePosition.value.x, mousePosition.value.y, radius, 0, Math.PI * 2);
                    context.closePath();
                    context.clip();

                    // Draw the zoomed-in area onto the main canvas
                    context.drawImage(
                        offscreenCanvas.value,
                        sx,
                        sy,
                        sWidth,
                        sHeight,
                        dx,
                        dy,
                        dWidth,
                        dHeight
                    );

                    context.restore();

                    // Add border around the zoomed-in circle
                    context.beginPath();
                    context.arc(mousePosition.value.x, mousePosition.value.y, radius, 0, Math.PI * 2);
                    context.closePath();
                    context.lineWidth = 2; // Set the border width
                    context.strokeStyle = 'black'; // Set the border color
                    context.stroke();
                }
            }

            // Mouse Events ***********************************
            function handleMouseMove(event:MouseEvent) {
                if (!canvas.value) return;

                // Update mouse position
                mousePosition.value.x = event.offsetX;
                mousePosition.value.y = event.offsetY;

                if ((shift_Pressed.value && w_Pressed.value) || tempStartPoint) {
                    canvas.value!.style.cursor = "crosshair";
                    hoverLine.value = null;
                    redrawCanvas();
                    return;
                }

                let doHover = false;

                for (const line of (lines.value?.lines ?? [])) {
                    for (const pointType of ["start", "middle", "end"] as const) {
                    const scaledPoint = toCanvasCoords(line[pointType]);
                    const dx = event.offsetX - scaledPoint.x;
                    const dy = event.offsetY - scaledPoint.y;
                    if (Math.sqrt(dx * dx + dy * dy) <= 5) {
                        canvas.value!.style.cursor = "pointer";
                        hoverLine.value = { line, pointType };
                        doHover = true;
                        break;
                    }
                    }
                    if (doHover) break;
                }

                // If we are not hoovering a point it resets hovering
                if (!doHover) {
                    canvas.value!.style.cursor = ""; // Default cursor
                    hoverLine.value = null; // Clear hoovered line
                }

                redrawCanvas();
            };

            function effectiveDrawPoints() {
                return showPoints.value && !hidePointsTemp.value;
            }

            function togglePoints() {
                if (props.readOnly) {
                    showPoints.value = !showPoints.value;
                    redrawCanvas();
                }
            }

            async function startDrag(event: PointerEvent) {
                if (props.readOnly) return;
                if (!props.image?.id) return;

                if (canvas.value) {
                    try { canvas.value.setPointerCapture(event.pointerId); } catch {}
                }

                const hovered = findPointAt(event.offsetX, event.offsetY);

                // SHIFT+Q: split line (De Casteljau)
                if (shift_Pressed.value && q_Pressed.value && hovered) {
                    const src = hovered.line;

                    const [firstPure, secondNew] = splitQuadratic(src, 0.5);

                    const firstUpdated: Line = { id: src.id, ...firstPure };

                    try {
                        clearDirtyFor(src.id);

                        // Mark local echo to avoid bouncing my own update
                        locallyUpdatedLines.add(firstUpdated.id);

                        // Update original line to assume first line
                        await ProjectService.updateAnnotation(props.image.id, [firstUpdated]);
    
                        // Insert second line with temporary id then replace
                        const tempId = Date.now();
                        const secondTmp: Line = { id: tempId, ...secondNew };

                        // local state (replace src by firstUpdated and add secondTmp)
                        const idx = lines.value.lines.findIndex(l => l.id === src.id);
                        if (idx >= 0) {
                            lines.value.lines.splice(idx, 1, firstUpdated);
                            lines.value.lines.splice(idx + 1, 0, secondTmp);
                        } else {
                            lines.value.lines.push(firstUpdated, secondTmp);
                        }

                        // Persist and replace tmp
                        const created = await ProjectService.insertAnnotation(props.image.id, {
                            label: secondNew.label,
                            start: secondNew.start,
                            middle: secondNew.middle,
                            end: secondNew.end,
                        });
                        const idx2 = lines.value.lines.findIndex(l => l.id === tempId);
                        if (idx2 >= 0) lines.value.lines[idx2] = created;

                        emit("onNewLine", created);
                        redrawCanvas();
                    } catch (e) {
                        console.error("Erro ao dividir linha (update+insert):", e);
                    }
                    return; // clique já tratado
                }

                // --- Se já temos 1º ponto (a desenhar a segunda âncora) ---
                if (tempStartPoint) {
                    // 2.º ponto: se estiver sobre um ponto existente, usa-o (ignora drag)
                    const endPoint: Point = hovered ? { ...hovered.line[hovered.pointType] }
                                                    : toOriginalCoords(event.offsetX, event.offsetY);

                    const tempId = Date.now();
                    const tempLine: Line = {
                        id: tempId,
                        label: String("-1"),
                        start: tempStartPoint,
                        middle: { x: (tempStartPoint.x + endPoint.x) / 2, y: (tempStartPoint.y + endPoint.y) / 2 },
                        end: endPoint,
                    };

                    try {
                        lines.value.lines.push(tempLine);
                        // Persist with server-generated id (pass Omit<Line,'id'>)
                        const created = await ProjectService.insertAnnotation(props.image.id, {
                            label: tempLine.label,
                            start: tempLine.start,
                            middle: tempLine.middle,
                            end: tempLine.end,
                        });

                        // Replace temporary by real row
                        const idx = lines.value.lines.findIndex(l => l.id === tempId);
                        if (idx >= 0) lines.value.lines[idx] = created;
                        emit("onNewLine", created);
                    } catch (err) {
                        console.error("Could not save line:", err);
                    } finally {
                        tempStartPoint = null;
                        draggedLine.value = null;
                        redrawCanvas();
                    }
                    return;
                }

                // --- SHIFT+W: 1.º ponto “encaixado” no ponto sob o rato (sem drag) ---
                if (shift_Pressed.value && w_Pressed.value && hovered) {
                    tempStartPoint = { ...hovered.line[hovered.pointType] };
                    redrawCanvas();
                    return;
                }

                // --- Fluxo normal existente (drag/delete OU começar nova linha) ---
                let handled = false;

                // 1) Detectar clique sobre ponto -> drag/delete (COMO TINHAS)
                lines.value?.lines.forEach((line, lineIndex) => {
                    if (handled) return;

                    (["start", "middle", "end"] as Array<"start" | "middle" | "end">).forEach((pointType) => {
                        if (handled) return;

                        const scaledPoint = toCanvasCoords(line[pointType]);
                        const dx = event.offsetX - scaledPoint.x;
                        const dy = event.offsetY - scaledPoint.y;
                        if (Math.sqrt(dx * dx + dy * dy) <= 5) {
                            if (deleteLineActive.value) {
                            lines.value?.lines.splice(lineIndex, 1);
                            handled = true;
                            ProjectService.deleteAnnotation(props.image!.id, line.id);
                            emit("onDeleteLine", line);
                            } else {
                            draggedLine.value = { line, pointType };
                            handled = true;
                            }
                        }
                    });
                });

                if (handled) {
                    redrawCanvas();
                    return;
                }

                // 2) Não clicou em ponto: começar nova linha -> definir 1º ponto
                tempStartPoint = toOriginalCoords(event.offsetX, event.offsetY);
                redrawCanvas();
            }

            function drag(event:PointerEvent) {
                if (props.readOnly) return;
                if (!canvas.value || !draggedLine.value) return;
                event.preventDefault(); // Make sure the window is not selected when dragging

                const { line, pointType } = draggedLine.value;

                // coordenadas do rato limitadas ao retângulo do canvas
                const rect = canvas.value.getBoundingClientRect();
                const cx = clamp(event.clientX - rect.left, 0, rect.width);
                const cy = clamp(event.clientY - rect.top, 0, rect.height);
                const newPos = toOriginalCoords(cx, cy);

                if (allLinesMoveActive.value) {
                    //const oldPos = line[pointType];
                    //const newPos = toOriginalCoords(event.offsetX, event.offsetY);
                    const oldPos = line[pointType];
                    // Calculates movement
                    const dx = newPos.x - oldPos.x;
                    const dy = newPos.y - oldPos.y;

                    // Apply movement to all lines
                    lines.value.lines.forEach((innerLine) => {
                        innerLine.start.x += dx;
                        innerLine.start.y += dy;
                        innerLine.middle.x += dx;
                        innerLine.middle.y += dy;
                        innerLine.end.x += dx;
                        innerLine.end.y += dy;
                        markDirty(innerLine);
                    });
                } else if (lineMoveActive.value) {
                    //const oldPos = line[pointType];
                    //const newPos = toOriginalCoords(event.offsetX, event.offsetY);
                    const oldPos = line[pointType];
                    const dx = newPos.x - oldPos.x;
                    const dy = newPos.y - oldPos.y

                    line.start.x += dx;
                    line.start.y += dy;
                    line.middle.x += dx;
                    line.middle.y += dy;
                    line.end.x += dx;
                    line.end.y += dy;

                    markDirty(line);
                } else {      
                    //const newPos = toOriginalCoords(event.offsetX, event.offsetY);

                    line[pointType].x = newPos.x;
                    line[pointType].y = newPos.y;

                    // If middle point is being dragged, no calculations necessary
                    if (pointType === "middle") {
                        markDirty(line);
                        redrawCanvas();
                        return;
                    }

                    // If start or end point is being dragged, we adjust the middle point automatically
                    const startPoint = line.start;
                    const endPoint = line.end;
                    line.middle.x = (startPoint.x + endPoint.x) / 2;
                    line.middle.y = (startPoint.y + endPoint.y) / 2;

                    markDirty(line);
                }

                redrawCanvas();
            }

            const stopDrag = () => {
                if (draggedLine.value) {
                    const alive = lines.value.lines.some(l => l.id === draggedLine.value!.line.id);
                    if (!alive) {
                    dirtyLines.value.delete(draggedLine.value!.line.id);
                    }
                }
                if (dirtyLines.value.size) flushDirty('mouseup');
                draggedLine.value = null;
            };

            // Keyboard Events *************************************
            function trackPressedKeys(event:KeyboardEvent) {
                if (props.readOnly) {
                    if (event.key === "Shift") {
                        shift_Pressed.value = true;
                    } else if (event.key.toLowerCase() === "z") {
                        z_Pressed.value = true;
                    }

                    if (shift_Pressed.value && z_Pressed.value) {
                        event.preventDefault();
                        zoomActive.value = true;
                        redrawCanvas();
                    }
                    return;
                }

                if (event.key === "Control") {
                    ctrl_Pressed.value = true;
                } else if (event.key === "Shift") {
                    shift_Pressed.value = true;
                } else if (event.key.toLowerCase() === "a") {
                    a_Pressed.value = true;
                } else if (event.key.toLowerCase() === "d") {
                    d_Pressed.value = true;                    
                } else if (event.key.toLowerCase() === "s") {
                    s_Pressed.value = true;
                } else if (event.key.toLowerCase() === "z") {
                    z_Pressed.value = true;
                } else if (event.key.toLowerCase() === "w") {
                    w_Pressed.value = true;
                } else if (event.key.toLowerCase() === "q") {
                    q_Pressed.value = true;
                } else if (event.key.toLowerCase() === "h") {
                    h_Pressed.value = true;
                }

                if (ctrl_Pressed.value && d_Pressed.value) {
                    event.preventDefault();
                    deleteLineActive.value = true;
                    redrawCanvas();
                }

                if (shift_Pressed.value && s_Pressed.value) {
                    event.preventDefault();
                    lineMoveActive.value = true;
                    redrawCanvas();
                }

                if (shift_Pressed.value && a_Pressed.value) {
                    event.preventDefault();
                    allLinesMoveActive.value = true;
                    redrawCanvas();
                }

                if (shift_Pressed.value && z_Pressed.value) {
                    event.preventDefault();
                    zoomActive.value = true;
                    redrawCanvas();
                }

                if (shift_Pressed.value && h_Pressed.value) {
                    event.preventDefault();
                    hidePointsTemp.value = true;
                    redrawCanvas();
                }                
            }

            function trackUnpressedKeys(event:KeyboardEvent) {
                if (props.readOnly) {
                    if (event.key === "Shift") {
                        shift_Pressed.value = false;
                        zoomActive.value = false;
                        redrawCanvas();
                    } else if (event.key.toLowerCase() === "z") {
                        z_Pressed.value = false;
                        zoomActive.value = false;
                        redrawCanvas();
                    }
                    return;
                }

                if (event.key === "Control") {
                    ctrl_Pressed.value = false;
                    deleteLineActive.value = false;
                } else if (event.key === "Shift") {
                    shift_Pressed.value = false;
                    lineMoveActive.value = false;
                    allLinesMoveActive.value = false;
                    zoomActive.value = false;
                } else if (event.key.toLowerCase() === "a") {
                    a_Pressed.value = false;
                    allLinesMoveActive.value = false;
                } else if (event.key.toLowerCase() === "d") {
                    d_Pressed.value = false;
                    deleteLineActive.value = false;
                } else if (event.key.toLowerCase() === "s") {
                    s_Pressed.value = false;
                    lineMoveActive.value = false;
                } else if (event.key.toLowerCase() === "z") {
                    z_Pressed.value = false;
                    zoomActive.value = false;
                } else if (event.key.toLowerCase() === "w") {
                    w_Pressed.value = false;
                } else if (event.key.toLowerCase() === "q") {
                    q_Pressed.value = false;
                } else if (event.key.toLowerCase() === "h") {
                    h_Pressed.value = false;
                }

                if (!shift_Pressed.value || !h_Pressed.value) {
                    if (hidePointsTemp.value) {
                        hidePointsTemp.value = false;
                        redrawCanvas();
                    }
                }                
                redrawCanvas();
            }

            function handleResize(evt?: Event) {
                if (!canvas.value || !img.value || !offscreenCanvas.value) return;

                const newWidth = img.value.clientWidth;
                const newHeight = img.value.clientHeight;

                if (newWidth === 0 || newHeight === 0) return;

                canvas.value.width = newWidth;
                canvas.value.height = newHeight;

                offscreenCanvas.value.width = newWidth;
                offscreenCanvas.value.height = newHeight;

                redrawCanvas();
            }

            function normalize(v: {x:number;y:number}) {
            const len = Math.hypot(v.x, v.y) || 1;
            return { x: v.x / len, y: v.y / len };
            }

            function roundedRectPath(ctx: CanvasRenderingContext2D, x:number, y:number, w:number, h:number, r=8) {
            const rr = Math.min(r, w/2, h/2);
            ctx.beginPath();
            ctx.moveTo(x+rr, y);
            ctx.lineTo(x+w-rr, y);
            ctx.quadraticCurveTo(x+w, y, x+w, y+rr);
            ctx.lineTo(x+w, y+h-rr);
            ctx.quadraticCurveTo(x+w, y+h, x+w-rr, y+h);
            ctx.lineTo(x+rr, y+h);
            ctx.quadraticCurveTo(x, y+h, x, y+h-rr);
            ctx.lineTo(x, y+rr);
            ctx.quadraticCurveTo(x, y, x+rr, y);
            ctx.closePath();
            }

            function clamp(n:number, min:number, max:number){ 
                return Math.max(min, Math.min(max, n));
            }

            // Global mouse to work in read-only (pointer-events: none)
            function handleGlobalMouseMove(e: MouseEvent) {
                if (!zoomActive.value || !canvas.value) return;

                const rect = canvas.value.getBoundingClientRect();
                const x = clamp(e.clientX - rect.left, 0, rect.width);
                const y = clamp(e.clientY - rect.top, 0, rect.height);

                mousePosition.value.x = x;
                mousePosition.value.y = y;

                redrawCanvas();
            }

            async function updateLineLabel(lineId: number, newLabel: string) {
                if (!props.image?.id) return;

                const line = lines.value.lines.find(l => l.id === lineId);
                if (!line) return;

                line.label = newLabel;

                clearDirtyFor(lineId);

                locallyUpdatedLines.add(lineId);

                await ProjectService.updateAnnotation(props.image.id, [line]);

                redrawCanvas();
            }

            expose({ updateLineLabel });

            function clearDirtyFor(id: number) {
                if (dirtyLines.value.size) dirtyLines.value.delete(id);
            }            

            // Global vue events
            onMounted(() => {
                initCanvas(); // Initialize canvas on startup
                window.addEventListener('pointermove', drag);
                window.addEventListener('mousemove', handleGlobalMouseMove);
                window.addEventListener('pointerup', stopDrag);
                window.addEventListener('keydown', trackPressedKeys);
                window.addEventListener('keyup', trackUnpressedKeys);                
                window.addEventListener('resize', handleResize);
            });

            onBeforeUnmount(() => {
                annoUnsubs.forEach(off => off());
                annoUnsubs = [];

                window.removeEventListener('pointermove', drag);
                window.removeEventListener('mousemove', handleGlobalMouseMove);
                window.removeEventListener('pointerup', stopDrag);
                window.removeEventListener('keydown', trackPressedKeys);
                window.removeEventListener('keyup', trackUnpressedKeys);
                window.removeEventListener('resize', handleResize);                
            });            

            return { canvas, initCanvas, redrawCanvas, startDrag, imgSrc, showPoints, togglePoints };
        }
    });
</script>

<style lang="scss">
    .imagecanvas-main {
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        width: 100%;
        height: 100%;
        position: relative;
    }

    .imagecanvas-container {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        height: 100%;
        background: #464646;
    }

    .imagecanvas-image-canvas-container {
        flex-grow: 1;
        display: flex;
        justify-content: center;
        align-items: center;
        margin-right: 5px;
        padding: 10px;
    }

    .image, .absolute-canvas {
        max-width: 100%;
        height: auto;
        user-select: none;
    }

    .absolute-canvas {
        position: absolute;
        //top: 0;
        //left: 0;
        cursor: crosshair;
    }

    .no-pointer-events {
        pointer-events: none;
    }

    .canvas-toolbar {
        position: absolute;
        top: 8px;
        right: 12px;
        display: flex;
        gap: 8px;
        z-index: 10;
        pointer-events: auto;
    }

    .canvas-toolbar .toolbar-btn {
        width: 24px;
        height: 24px;
        padding: 3px;
        border: 1px solid #4d4d4d;
        background: #2b2b2b;
        border-radius: 6px;
        cursor: pointer;
        opacity: .92;
    }

    .canvas-toolbar .toolbar-btn:hover {
        background: #3a3a3a;
    }    
</style>